/*======================================================================
MAISON FOOTER — Reveal + Follow on Shop theming

Visual Evolution — Cambio #5 (P1): unifica el Footer bajo el mismo
sistema de entrada que el resto del sitio (.maison-reveal,
06_maison-animations.css) en vez del cascade nativo de Dawn
(scroll-trigger / data-cascade, ver footer.liquid).

El observer de .maison-reveal que ya existe vive en discover-essence.js
pero está scopeado a su propia sección (this.section.querySelectorAll),
no cubre el Footer — este archivo es el mínimo necesario para que
.maison-reveal funcione aquí sin duplicar ese motor ni tocar
discover-essence.js (congelado).

Cada bloque se revela una sola vez: un footer no debe esconderse de
nuevo al hacer scroll hacia arriba — sería ruido, no motion con
intención.

Follow on Shop ({{ shop | login_button: action: 'follow' }}) renderiza
<shop-follow-button> (confirmado inspeccionando el DOM real — NO
<shop-login-button>; ese nombre era una suposición de la documentación
de la comunidad sobre otro flujo y nunca coincidió con el markup real
de este botón, por eso el theming anterior nunca tenía efecto: el
selector simplemente no encontraba nada). Es un Web Component con
shadow DOM cerrado a CSS de página — no existe forma de recolorearlo
desde maison-footer.css, es una limitación de la plataforma, no de
especificidad. Shopify no documenta una custom property pública para
temarlo. La única vía real es inyectar un <style> dentro de su shadow
root (y de cualquier shadow root anidado que contenga, si los hay —
walk() no asume una profundidad fija). Como el componente monta de
forma asíncrona y sin evento público, se reintenta con backoff corto y
acotado en vez de un MutationObserver anidado por nivel — más simple,
mismo resultado, y se detiene solo. Las custom properties
(--ms-color-primary) sí atraviesan shadow boundaries, así que el
<style> inyectado puede reusar el token real de Maison UI en vez de un
hex nuevo. Si el shadow root resultara `closed` (no expuesto en
el.shadowRoot), esta técnica no tiene forma de alcanzarlo — es un límite
real de la plataforma, no algo que JS pueda rodear.

Version 1.2
======================================================================*/

document.addEventListener("DOMContentLoaded", () => {
  const items = document.querySelectorAll(".footer .maison-reveal");

  if (items.length) {
    if ("IntersectionObserver" in window) {
      /* .maison-reveal-armed es lo que activa el estado oculto animado
         (maison-footer.css, 07. REVEAL/MOTION) — se añade justo antes de
         observar, nunca antes. Sin esta clase el contenido ya es visible
         por CSS; JS solo la agrega cuando de verdad va a completar el
         ciclo de reveal, así el observer nunca es requisito para que el
         footer exista visualmente, solo para animarlo. */
      items.forEach((item) => item.classList.add("maison-reveal-armed"));

      const observer = new IntersectionObserver(
        (entries, obs) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;

            entry.target.classList.add("is-visible");
            obs.unobserve(entry.target);
          });
        },
        { threshold: 0.1, rootMargin: "0px 0px -10% 0px" },
      );

      items.forEach((item) => observer.observe(item));
    }
  }

  themeFollowOnShopButton();
});

function themeFollowOnShopButton() {
  const host = document.querySelector(".footer__follow-on-shop shop-follow-button");

  if (!host) return;

  /* El color ya funciona (auditoría anterior); el rectángulo exterior es
     un efecto secundario de CÓMO se aplicó: pintar background-color en
     :host, button, a, .follow-text Y [class*="follow"] a la vez dora
     TODOS esos elementos por separado — si el botón real (button) está
     anidado dentro de :host u otro contenedor, cada uno queda con su
     propia caja dorada con su propio padding/radius, y se ven como dos
     cápsulas una dentro de otra. Sin acceso al DOM real no se puede
     confirmar cuál de los dos ES la superficie visual, así que en vez
     de adivinar UNA, se fuerza a :host (el wrapper que expone el custom
     element hacia la página) a no tener caja propia — fondo y borde en
     transparent, sin padding — dejando que solo el elemento interno que
     Shopify pinte (button si existe, si no la superficie con la que
     matchee [class*="follow"]) muestre la cápsula dorada real. */
  const styleContent = `
    :host {
      background-color: transparent !important;
      background: none !important;
      border: none !important;
      box-shadow: none !important;
      padding: 0 !important;
    }

    button, a.follow-text, [class*="follow" i] {
      background-color: var(--ms-color-primary) !important;
      border-color: var(--ms-color-primary) !important;
      color: var(--ms-color-heading) !important;
      fill: var(--ms-color-heading) !important;
    }

    /* Si .follow-text es un span de solo texto DENTRO del button (no la
       superficie del botón en sí), pintarlo también crearía la misma
       doble-caja a otra escala — se deja sin fondo propio, solo hereda
       el color de texto/ícono de la regla de arriba. */
    .follow-text:not(button):not(a) {
      background-color: transparent !important;
    }
  `;

  let injectedAnywhere = false;
  let sawAnyShadowRoot = false;

  const injectInto = (root) => {
    if (!root) return;

    if (root.querySelector("style[data-maison-follow-theme]")) {
      injectedAnywhere = true;
      return;
    }

    try {
      const style = document.createElement("style");
      style.setAttribute("data-maison-follow-theme", "");
      style.textContent = styleContent;
      root.appendChild(style);
      injectedAnywhere = true;
    } catch (error) {
      /* Shadow root cerrado o estructura distinta a la esperada: no
         romper Follow on Shop por un fallo de tema visual. */
    }
  };

  const walk = (el, depth) => {
    if (!el || depth > 4) return;

    const root = el.shadowRoot;
    if (!root) return;

    sawAnyShadowRoot = true;
    injectInto(root);
    root.querySelectorAll("*").forEach((child) => walk(child, depth + 1));
  };

  let attempts = 0;
  const maxAttempts = 12;
  const tryTheme = () => {
    attempts += 1;
    walk(host, 0);

    if (injectedAnywhere) return;

    if (attempts < maxAttempts) {
      setTimeout(tryTheme, 300);
      return;
    }

    /* Documentado, no simulado: si tras 12 intentos (3.6s) nunca se vio
       un shadowRoot en <shop-follow-button>, el elemento existe pero su
       shadow root es `closed` (o Shopify cambió el componente) — un
       límite real de la plataforma que JS de página no puede rodear. */
    if (!sawAnyShadowRoot) {
      console.info(
        "[Maison Footer] shop-follow-button no expone shadowRoot abierto — " +
          "no es posible recolorearlo desde CSS ni JS de página (shadow DOM cerrado).",
      );
    }
  };
  tryTheme();
}
